import { ScanVaultService } from './services';

export interface SoftwareEcosystemSummary {
  ecosystem: string;
  resource_count: number;
  installation_count: number;
  package_count: number;
  /** Distinct package names; package_count counts distinct versions. */
  name_count?: number;
  last_observed_at: string;
}

/** Second inventory level: OS family, technology type, AI/ML kind or
 * library language. */
export interface SoftwareSubcategorySummary {
  subcategory: string;
  resource_count: number;
  installation_count: number;
  package_count: number;
  name_count?: number;
  last_observed_at: string;
}

export interface SoftwareCategorySummary {
  category: string;
  resource_count: number;
  installation_count: number;
  package_count: number;
  /** Distinct package names; package_count counts distinct versions. */
  name_count?: number;
  ecosystems: SoftwareEcosystemSummary[];
  subcategories?: SoftwareSubcategorySummary[];
}

export interface SoftwareInventorySummary {
  snapshot_mode: string;
  resources_with_software: number;
  installation_count: number;
  package_count: number;
  /** Distinct package names; package_count counts distinct versions. */
  name_count?: number;
  last_observed_at?: string;
  categories: SoftwareCategorySummary[];
  ecosystems: string[];
  resource_types: string[];
}

export interface SoftwarePackageItem {
  category: string;
  subcategory?: string;
  ecosystem: string;
  name: string;
  version: string;
  purl?: string;
  licenses: string[];
  resource_count: number;
  installation_count: number;
  /** Distinct versions behind this item; >1 only for name-level rows. */
  version_count?: number;
  last_observed_at: string;
  /** Per-version rows only, from the lifecycle catalog. */
  eol_date?: string;
  latest_version?: string;
  /** Technologies: the product, e.g. mysql / MySQL for mysql-server-8.0. */
  technology_key?: string;
  technology_name?: string;
  /** Name-level technology rows: the package names behind the product. */
  package_names?: string[];
}

export interface SoftwarePackagePage {
  items: SoftwarePackageItem[];
  next_cursor?: string;
  total_count: number | null;
  count_mode: string;
}

export interface SoftwareInstallationItem {
  category: string;
  subcategory?: string;
  ecosystem: string;
  name: string;
  version: string;
  licenses: string[];
  purl?: string;
  last_observed_at: string;
  path?: string;
  resource_uuid: string;
  component_key: number;
  resource_type: string;
  native_id: string;
  cloud: string;
  account_native_id: string;
  account_uuid: string;
  environment_id: string;
  region: string;
  resource_name: string;
  arn: string;
  resource_last_seen_at: string;
}

export interface SoftwareInstallationPage {
  items: SoftwareInstallationItem[];
  next_cursor?: string;
  total_count: number | null;
  count_mode: string;
}

export interface SoftwareInventoryFilters {
  environmentId?: string;
  accountId?: string;
  resourceType?: string;
  resourceUuid?: string;
  category?: string;
  ecosystem?: string;
  subcategory?: string;
  /** Technology key; name-level technology rows span package names. */
  technology?: string;
  search?: string;
  name?: string;
  version?: string;
  purl?: string;
  limit?: number;
  cursor?: string;
  /** Packages only: `name` returns one row per package with exact counts. */
  groupBy?: 'name' | 'version';
}

const one = (value?: string) => (value ? [value] : undefined);

async function generated<T>(request: Promise<{ data: T }>): Promise<T> {
  try {
    return (await request).data;
  } catch {
    throw new Error('Software inventory is temporarily unavailable.');
  }
}

// The generated client only gains `technology` with @ariksa/scan-vault
// 0.1.14; until then it travels as an extra Axios query parameter.
function requestOptions(signal?: AbortSignal, technology?: string) {
  if (!signal && !technology) return undefined;
  return {
    ...(signal ? { signal } : {}),
    ...(technology ? { params: { technology } } : {}),
  };
}

async function getSummary(
  filters: SoftwareInventoryFilters,
  signal?: AbortSignal,
): Promise<SoftwareInventorySummary> {
  return generated(
    ScanVaultService.Vulnerability.getSoftwareInventorySummary(
      {
        environmentId: one(filters.environmentId),
        accountUuid: one(filters.accountId),
        resourceType: one(filters.resourceType),
        category: one(filters.category),
        ecosystem: one(filters.ecosystem),
        subcategory: one(filters.subcategory),
        search: filters.search,
      },
      requestOptions(signal, filters.technology),
    ),
  );
}

async function getPackages(
  filters: SoftwareInventoryFilters,
  signal?: AbortSignal,
): Promise<SoftwarePackagePage> {
  return generated(
    ScanVaultService.Vulnerability.listSoftwareInventoryPackages(
      {
        cursor: filters.cursor,
        limit: filters.limit,
        environmentId: one(filters.environmentId),
        accountUuid: one(filters.accountId),
        resourceType: one(filters.resourceType),
        category: one(filters.category),
        ecosystem: one(filters.ecosystem),
        subcategory: one(filters.subcategory),
        groupBy: filters.groupBy,
        resourceUuid: one(filters.resourceUuid),
        search: filters.search,
      },
      requestOptions(signal, filters.technology),
    ),
  );
}

async function getInstallations(
  filters: SoftwareInventoryFilters,
  signal?: AbortSignal,
): Promise<SoftwareInstallationPage> {
  return generated(
    ScanVaultService.Vulnerability.listSoftwareInventoryInstallations(
      {
        cursor: filters.cursor,
        limit: filters.limit,
        category: filters.category,
        ecosystem: filters.ecosystem,
        subcategory: one(filters.subcategory),
        name: filters.name,
        version: filters.version,
        purl: filters.purl,
        environmentId: one(filters.environmentId),
        accountUuid: one(filters.accountId),
        resourceType: one(filters.resourceType),
        resourceUuid: one(filters.resourceUuid),
        search: filters.search,
      },
      requestOptions(signal, filters.technology),
    ),
  );
}

export const ScanVaultSoftwareService = {
  getSummary: (filters: SoftwareInventoryFilters = {}, signal?: AbortSignal) =>
    getSummary(filters, signal),
  getPackages: (filters: SoftwareInventoryFilters = {}, signal?: AbortSignal) =>
    getPackages(filters, signal),
  getInstallations: (
    filters: SoftwareInventoryFilters = {},
    signal?: AbortSignal,
  ) => getInstallations(filters, signal),
  getResourcePackages: (
    resourceUUID: string,
    filters: Omit<SoftwareInventoryFilters, 'accountId' | 'environmentId'> = {},
    signal?: AbortSignal,
  ) =>
    generated(
      ScanVaultService.Vulnerability.listResourceSoftwareInventoryPackages(
        {
          resourceUuid: resourceUUID,
          cursor: filters.cursor,
          limit: filters.limit,
          category: filters.category,
          ecosystem: filters.ecosystem,
        },
        requestOptions(signal),
      ),
    ),
};
